<template>
  <draggable
    v-bind="dragOptions"
    v-model="fields"
    class="listInput"
    :class="isDragging ? 'isDragging' : ''"
    @change="capture"
    @start="isDragging = true"
    @end="isDragging = false"
    item-key="id"
  >
    <template #item="{ element, index }">
      <div class="row" :key="'item-' + index">
        <div class="content">
          <input
            :id="name + 'Field-' + index"
            ref="input"
            v-model.trim="element.value"
            type="text"
            :placeholder="index > 0 ? 'Add another...' : `Add ${item}...`"
            :class="['sentry-masked-text', index === 0 ? 'focusPlease' : '', limit && element.value.length > limit ? 'over' : 'ok']"
            :maxlength="limit"
            @keyup.prevent="onInput"
            @keyup.enter.prevent="handleEnter($event, index)"
            @input="onInput"
          />
          <app-text-limit-message v-if="limit" :value="element.value" :limit="limit" :warn-limit="warnLimit" />
          <div v-if="element.value !== '' && fields.length > 2" class="dragHandle" aria-hidden="true">
            <span class="visuallyHidden">Drag</span>
            <app-icon icon="drag" />
          </div>
        </div>
      </div>
    </template>
  </draggable>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import draggable from 'vuedraggable';
import AppIcon from '@/components/AppIcon.vue';
import AppTextLimitMessage from '@/components/AppTextLimitMessage.vue';

const emit = defineEmits(['capture', 'save']);

const props = defineProps({
  fieldData: {
    type: Array,
  },
  name: {
    type: String,
  },
  item: {
    type: String,
  },
  limit: {
    type: Number,
  },
  maxItems: {
    type: Number,
    default: 0,
  },
  warnLimit: {
    type: Number,
  },
});

const fields = ref([{ value: '' }]);
const isDragging = ref(false);
const dragOptions = computed(() => {
  return {
    animation: 100,
    easing: 'ease',
    ghostClass: 'item-ghost',
    chosenClass: 'item-chosen',
    dragClass: 'item-drag',
    direction: 'vertical',
    handle: '.dragHandle',
  };
});

watch(
  () => props.fieldData,
  (newVal) => {
    if (newVal && newVal.length > 0) {
      fields.value = newVal.map((v) => {
        return { value: v };
      });
    } else {
      fields.value = [{ value: '' }];
    }
    checkFields();
  },
  { immediate: true, deep: true },
);

onMounted(() => {
  if (props.fieldData && props.fieldData.length > 0) {
    fields.value = props.fieldData.map((v) => {
      return { value: v };
    });
  } else {
    fields.value = [{ value: '' }];
  }
  checkFields();
});

function capture() {
  return new Promise((resolve) => {
    emit(
      'capture',
      fields.value.map((f) => f.value).filter((f) => f !== ''),
    );
    resolve();
  });
}
function handleEnter($event, i) {
  if ($event.shiftKey) {
    nextField(i);
  } else {
    save();
  }
}
function save() {
  capture().then(() => {
    emit('save');
  });
}
function onInput() {
  checkFields().then(capture);
}

function nextField(i) {
  if (fields.value[i + 1]) {
    const name = props.name + 'Field-' + (i + 1);
    let elem = document.getElementById(name);
    if (elem) {
      elem.focus();
    }
  }
}
function checkFields() {
  return new Promise((resolve) => {
    fields.value = fields.value.filter((f) => f.value !== '');

    if (fields.value.length === 0) {
      fields.value = [{ value: '' }];
    }

    if (fields.value[fields.value.length - 1].value !== '') {
      if (!props.maxItems || (props.maxItems && fields.value.length < props.maxItems)) {
        fields.value = [...fields.value, { value: '' }];
      }
    }

    resolve();
  });
}
</script>

<style lang="scss">
.listInput {
  .flip-list-move {
    transition: transform 0.5s;
  }
  .no-move {
    transition: transform 0s;
  }
  .item-ghost {
    opacity: 0.5;
  }
  .item-chosen {
    opacity: 1;
    .dragHandle {
      opacity: 1;
    }
  }
  .item-drag {
    opacity: 0;
  }
  .dragHandle {
    padding: rem(10px) 0 0 1em;
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: none;
    align-items: center;
    //transition: all 0.3s ease;
    svg {
      height: rem(10px);
      width: rem(13px);
    }
  }

  .row:hover .dragHandle,
  .dragHandle:focus,
  .dragHandle:active {
    display: flex;
    cursor: grab;
  }

  &.isDragging {
    & * {
      cursor: grabbing;
    }

    .row:hover .dragHandle {
      cursor: grabbing;
      display: none;
    }
    .item-chosen .dragHandle,
    .item-chosen.item-ghost .dragHandle {
      display: flex;
      cursor: grabbing;
    }
  }
}
</style>
